import React, { useEffect, useState } from 'react';
import apiService from '../../../services/apiService';

interface ActivityTimelineProps {
  lead: any;
  onClose: () => void;
}

const ACTION_ICONS: Record<string, string> = {
  lead_created: '✨',
  lead_assigned: '👤',
  status_change: '🔄',
  note: '📝',
  call: '📞',
  meeting: '🤝',
  follow_up: '⏰',
};

const ActivityTimeline: React.FC<ActivityTimelineProps> = ({ lead, onClose }) => {
  const [activities, setActivities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');
  const [noteType, setNoteType] = useState('note');
  const [saving, setSaving] = useState(false);

  const [followUpDate, setFollowUpDate] = useState('');

  const userStr = localStorage.getItem('aswUser');
  const currentUser = userStr ? JSON.parse(userStr) : null;

  const LEAD_STAGES = [
    { status: 'New Lead', label: 'Captured' },
    { status: 'Contacted', label: 'Contacted' },
    { status: 'Interested', label: 'Interested' },
    { status: 'Follow-up', label: 'Follow-up' },
    { status: 'Converted', label: 'Converted' }
  ];

  const getStageIndex = (status: string) => {
    if (status === 'Converted' || status === 'Booked') return 4;
    if (status === 'Follow-up') return 3;
    if (status === 'Interested') return 2;
    if (status === 'Contacted') return 1;
    if (status === 'New Lead') return 0;
    return -1;
  };

  const currentStageIndex = getStageIndex(lead.status);
  const isNotInterested = lead.status === 'Not Interested';

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const res = await apiService.getCrmActivities(lead.id);
      if (res.success) setActivities(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchActivities(); }, [lead.id]);

  const handleAddNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!note.trim() && !followUpDate) return;
    setSaving(true);
    try {
      const descriptionText = note.trim()
        ? note + (followUpDate ? ` (Follow-up scheduled for ${new Date(followUpDate).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })})` : '')
        : `Scheduled follow-up for ${new Date(followUpDate).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`;

      await apiService.logCrmActivity({
        action_type: noteType,
        entity_type: 'lead',
        entity_id: lead.id,
        description: descriptionText,
        performed_by: currentUser?.id
      });

      if (followUpDate) {
        await apiService.updateCrmLead(lead.id, {
          follow_up_date: new Date(followUpDate).toISOString(),
          performed_by: currentUser?.id
        });
      }

      setNote('');
      setFollowUpDate('');
      fetchActivities();
    } catch (e) { alert('Failed to log activity'); }
    finally { setSaving(false); }
  };

  const formatTime = (ts: string) => {
    const d = new Date(ts);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) + 
      ' ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="crm-modal-overlay" onClick={onClose}>
      <div 
        className="crm-modal"
        style={{ minWidth: '500px', maxWidth: '620px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="crm-modal-header" style={{ paddingBottom: '12px' }}>
          <div>
            <h3 style={{ marginBottom: 2 }}>Activity Timeline</h3>
            <div style={{ fontSize: '13px', color: 'var(--crm-text-muted)' }}>{lead.customer_name} — {lead.phone}</div>
          </div>
          <button className="crm-modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Journey Tracker */}
        <div style={{ 
          background: '#F8FAFC', 
          border: '1px solid var(--crm-border)', 
          borderRadius: '8px', 
          padding: '16px 20px 12px', 
          marginBottom: '16px',
          boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.01)'
        }}>
          <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            
            {/* Background Line */}
            <div style={{ 
              position: 'absolute', 
              left: '16px', 
              right: '16px', 
              top: '14px', 
              height: '3px', 
              background: '#E2E8F0', 
              zIndex: 0 
            }}></div>
            
            {/* Active Fill Line */}
            {!isNotInterested && currentStageIndex >= 0 && (
              <div style={{ 
                position: 'absolute', 
                left: '16px', 
                top: '14px', 
                height: '3px', 
                background: 'linear-gradient(90deg, #10B981, var(--crm-primary))',
                width: `${(currentStageIndex / (LEAD_STAGES.length - 1)) * 92}%`,
                transition: 'width 0.3s ease',
                zIndex: 0 
              }}></div>
            )}

            {isNotInterested && (
              <div style={{ 
                position: 'absolute', 
                left: '16px', 
                top: '14px', 
                height: '3px', 
                background: '#EF4444',
                width: '100%',
                zIndex: 0 
              }}></div>
            )}

            {/* Stage Nodes */}
            {LEAD_STAGES.map((stage, idx) => {
              const isCompleted = !isNotInterested && idx < currentStageIndex;
              const isActive = !isNotInterested && idx === currentStageIndex;
              
              let nodeBg = 'white';
              let nodeBorder = '2px solid #CBD5E1';
              let nodeColor = '#64748B';
              let shadow = 'none';

              if (isCompleted) {
                nodeBg = '#10B981';
                nodeBorder = '2px solid #10B981';
                nodeColor = 'white';
              } else if (isActive) {
                nodeBg = 'var(--crm-primary)';
                nodeBorder = '2px solid var(--crm-primary)';
                nodeColor = 'white';
                shadow = '0 0 0 3px var(--crm-primary-light)';
              } else if (isNotInterested && idx === LEAD_STAGES.length - 1) {
                nodeBg = '#EF4444';
                nodeBorder = '2px solid #EF4444';
                nodeColor = 'white';
                shadow = '0 0 0 3px #FEE2E2';
              }

              return (
                <div key={stage.status} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 1, width: '60px' }}>
                  {/* Node Circle */}
                  <div style={{
                    width: '28px', height: '28px', borderRadius: '50%',
                    background: nodeBg, border: nodeBorder, color: nodeColor,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '11px', fontWeight: 'bold', boxShadow: shadow,
                    transition: 'all 0.3s ease'
                  }}>
                    {isCompleted ? '✓' : (isNotInterested && idx === LEAD_STAGES.length - 1) ? '✕' : idx + 1}
                  </div>
                  {/* Label */}
                  <span style={{ 
                    fontSize: '10px', 
                    fontWeight: (isActive || (isNotInterested && idx === LEAD_STAGES.length - 1)) ? '700' : '500',
                    color: (isActive || isCompleted) ? 'var(--crm-text-primary)' : isNotInterested && idx === LEAD_STAGES.length - 1 ? '#EF4444' : 'var(--crm-text-muted)',
                    marginTop: '6px',
                    textAlign: 'center',
                    whiteSpace: 'nowrap'
                  }}>
                    {isNotInterested && idx === LEAD_STAGES.length - 1 ? 'Dropped' : stage.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Add Note/Call/Follow-up */}
        <form onSubmit={handleAddNote} style={{ padding: '0 0 16px', borderBottom: '1px solid var(--crm-border)', marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <select
              value={noteType}
              onChange={e => setNoteType(e.target.value)}
              style={{ padding: '7px 10px', border: '1.5px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', fontSize: '13px', background: 'white' }}
            >
              <option value="note">📝 Note</option>
              <option value="call">📞 Call</option>
              <option value="meeting">🤝 Meeting</option>
              <option value="follow_up">⏰ Follow-up</option>
            </select>
            <input
              type="text"
              placeholder="What happened? Add a log entry..."
              value={note}
              onChange={e => setNote(e.target.value)}
              style={{ flex: 1, minWidth: '200px', padding: '7px 11px', border: '1.5px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', fontSize: '13px' }}
            />
            <button type="submit" className="crm-btn crm-btn-primary" disabled={saving || (!note.trim() && !followUpDate)} style={{ padding: '7px 14px' }}>
              {saving ? '…' : '+ Log Activity'}
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: 'var(--crm-text-secondary)', background: '#F8FAFC', padding: '6px 10px', borderRadius: '6px', border: '1px solid #E2E8F0' }}>
            <span style={{ fontWeight: 600 }}>⏰ Next Follow-up (Optional):</span>
            <input
              type="datetime-local"
              value={followUpDate}
              onChange={e => setFollowUpDate(e.target.value)}
              style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '4px', border: '1px solid #CBD5E1', outline: 'none' }}
            />
            {followUpDate && (
              <button
                type="button"
                onClick={() => setFollowUpDate('')}
                style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: '12px', fontWeight: 600, padding: 0 }}
              >
                Clear
              </button>
            )}
          </div>
        </form>

        {/* Timeline */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <div className="crm-loading"><div className="crm-spinner"></div> Loading...</div>
          ) : activities.length === 0 ? (
            <div className="crm-empty-state">
              <div className="crm-empty-state-icon">📋</div>
              <h3>No activity yet</h3>
              <p>Log a call or note above.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {activities.map((a: any, i: number) => (
                <div key={a.id} style={{ display: 'flex', gap: '12px', padding: '10px 0', borderBottom: i < activities.length - 1 ? '1px solid var(--crm-border-light)' : 'none' }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%',
                    background: 'var(--crm-primary-light)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    fontSize: '14px', flexShrink: 0, marginTop: 2
                  }}>
                    {ACTION_ICONS[a.action_type] || '📌'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13.5px', color: 'var(--crm-text-primary)', lineHeight: 1.4 }}>{a.description}</div>
                    <div style={{ fontSize: '11.5px', color: 'var(--crm-text-muted)', marginTop: 4, display: 'flex', gap: 10 }}>
                      <span>{a.performed_by?.name || 'System'}</span>
                      <span>•</span>
                      <span>{formatTime(a.created_at)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivityTimeline;
